import { all, takeLatest, call, put } from 'redux-saga/effects';
import { ActionType } from 'deox';
import membershipService from '@/services/membership';
import { IdentityUsersInstance } from '@/services/identity/users';
import env from '@/env';
import authHelpers from '@/services/auth/helpers';
import { navigate } from '@reach/router';
import {
  fetchMembersActions,
  getUserInfoActions,
  deleteMemberActions,
  updateUserInfoActions,
  updateUserPasswordActions,
} from '../actions/membership';
import { getSubdomain, organisationToSubdomain } from '@/utils';

export function* fetchMembersSaga(action: ActionType<typeof fetchMembersActions.request>) {
  try {
    const { organisationId, searchText, pageSize, pageNumber } = action.payload;
    const { data, paging } = yield call(
      membershipService.fetchMembers,
      organisationId,
      searchText,
      pageSize,
      pageNumber,
    );
    yield put(fetchMembersActions.success(data, paging));
  } catch (err) {
    yield put(fetchMembersActions.failure(err));
  }
}

export function* deleteMemberSaga(action: ActionType<typeof deleteMemberActions.request>) {
  try {
    const { organisationId, memberId } = action.payload;

    yield call(membershipService.deleteMember, organisationId, memberId);
    yield put(deleteMemberActions.success());
    yield put(fetchMembersActions.request(organisationId));
  } catch (err) {
    yield put(deleteMemberActions.failure(err));
  }
}

export function* getUserInfoSaga() {
  try {
    const {
      data: { email, memberships, userId, firstName, lastName },
    } = yield call(membershipService.getUserInfo);
    if (!memberships.length) {
      navigate('/auth/bank-detail');
    } else {
      const subdomain = getSubdomain();
      const organisationCurrent = memberships.find((org: { organisationName: string }) => {
        return organisationToSubdomain(org.organisationName) === subdomain;
      });
      if (!organisationCurrent) {
        const organisationsOwner = memberships.filter(
          (org: { roleName: string }) => org.roleName?.toLowerCase() === env.roles.organisationOwner.toLowerCase(),
        );
        const organisationShouldAccess = !organisationsOwner.length
          ? memberships[0]
          : organisationsOwner[0];
        authHelpers.storeOrgToken(organisationShouldAccess.token);
      } else {
        if (organisationCurrent) authHelpers.storeOrgToken(organisationCurrent.token);
      }
    }
    yield put(getUserInfoActions.success(email, memberships, userId, firstName, lastName));
  } catch (err) {
    yield put(getUserInfoActions.failure(err));
  }
}

export function* updateUserInfoSaga(action: ActionType<typeof updateUserInfoActions.request>) {
  const {
    firstName: firstNameRequest,
    lastName: lastNameRequest,
    userId: userIdRequest,
  } = action.payload;
  try {
    const {
      data: { email, memberships, userId, firstName, lastName },
    } = yield call(
      membershipService.updateUserInfo,
      firstNameRequest,
      lastNameRequest,
      userIdRequest,
    );
    yield put(updateUserInfoActions.success());
    yield put(getUserInfoActions.success(email, memberships, userId, firstName, lastName));
  } catch (err) {
    yield put(updateUserInfoActions.failure(err));
  }
}

export function* updateUserPasswordSaga(
  action: ActionType<typeof updateUserPasswordActions.request>,
) {
  const { password, currentPassword } = action.payload;
  try {
    yield call(IdentityUsersInstance.updateUserPassword, currentPassword, password);
    yield put(updateUserPasswordActions.success());
  } catch (err) {
    yield put(updateUserPasswordActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchMembersActions.request.type, fetchMembersSaga),
    takeLatest(deleteMemberActions.request.type, deleteMemberSaga),
    takeLatest(getUserInfoActions.request, getUserInfoSaga),
    takeLatest(updateUserInfoActions.request, updateUserInfoSaga),
    takeLatest(updateUserPasswordActions.request, updateUserPasswordSaga),
  ]);
}
